/** Game-run API: history list, single-run fetch (resume/review), and the fire-and-forget autosave
 *  used by the game's debounced effect. */
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./client";

export interface RunSummary {
  id: string;
  status: string;
  mode: string;
  scenarioIndex: number;
  endingId: string | null;
  scores: Record<string, number> | null;
  startedAt: string;
  finishedAt: string | null;
  updatedAt: string;
}

export interface RunDetail extends RunSummary {
  seed: number | null;
  phase: string | null;
  snapshot: unknown;
  transcript: unknown;
}

export function useRuns(enabled: boolean) {
  return useQuery({
    queryKey: ["runs"],
    queryFn: () => apiFetch<{ runs: RunSummary[] }>("/api/runs").then((r) => r.runs),
    enabled,
  });
}

export function useRun(id: string | null) {
  return useQuery({
    queryKey: ["runs", id],
    enabled: !!id,
    queryFn: () => apiFetch<{ run: RunDetail }>(`/api/runs/${id}`).then((r) => r.run),
  });
}

export interface AutosavePayload {
  snapshot: unknown;
  status: "in_progress" | "finished";
  mode: "timed" | "untimed";
  seed?: number;
  scenarioIndex: number;
  phase?: string | null;
  endingId?: string | null;
  scores?: Record<string, number>;
  transcript?: unknown[];
}

/** Persist a run to the backend (debounced caller in the game). Best-effort: caller handles failures. */
export async function autosaveRun(runId: string, payload: AutosavePayload): Promise<void> {
  await apiFetch(`/api/runs/${runId}/autosave`, { method: "PUT", body: payload });
}

/** Delete a run ("exit practice without saving"). Best-effort. */
export async function deleteRun(runId: string): Promise<void> {
  await apiFetch(`/api/runs/${runId}`, { method: "DELETE" });
}
